import { BaseCompanyIdProps, BaseCompanyProps } from "@custom-types/base-types";
import { Grid } from "@mui-wrapper";
import { DeleteWorkspaceStep1 } from "@pages/workspace-profile/tab-contents/workspace-info-tab/delete-workspace/delete-workspace-step1";
import { DeleteWorkspaceStep2 } from "@pages/workspace-profile/tab-contents/workspace-info-tab/delete-workspace/delete-workspace-step2";
import { DeleteWorkspaceStep3 } from "@pages/workspace-profile/tab-contents/workspace-info-tab/delete-workspace/delete-workspace-step3";
import { CompanyProfileTab } from "@router/route-params";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { ReactSetStateFunction } from "@utils/utility-types";
import { InternalLinkButton } from "@components/links/internal-links";
import { Text } from "@flat-ui";

interface Props extends BaseCompanyProps {
  /** Setter for disableContinue */
  setShouldDisableContinueButton: ReactSetStateFunction<boolean>;

  /** Response from delete workspace API */
  deleteWorkspaceResponse?: FaroCoreApiTypes.DeleteCompanyResponse;
}

/** Returns the steps for deleting a workspace */
export function deleteWorkspaceSteps({
  company,
  setShouldDisableContinueButton,
  deleteWorkspaceResponse,
}: Props): Array<{ label: string; content: React.JSX.Element }> {
  return [
    {
      label: "Review Deletion Guidelines",
      content: (
        <DeleteWorkspaceStep1
          companyId={company.id}
          setShouldDisableContinueButton={setShouldDisableContinueButton}
          deleteWorkspaceResponse={deleteWorkspaceResponse}
        />
      ),
    },
    {
      label: "Workspace's details",
      content: (
        <DeleteWorkspaceStep2
          companyId={company.id}
          setShouldDisableContinueButton={setShouldDisableContinueButton}
        />
      ),
    },
    {
      label: "Final Confirmation",
      content: (
        <DeleteWorkspaceStep3
          workspaceName={company.name}
          setShouldDisableContinueButton={setShouldDisableContinueButton}
        />
      ),
    },
  ];
}

interface EntityReportProps extends BaseCompanyIdProps {
  /** The company profile tab that the button will open */
  tab: CompanyProfileTab;

  /** Label for the type of data that will be deleted, used for display purposes */
  label: "groups" | "projects";

  /** Response from delete workspace API */
  deleteWorkspaceResponse?: FaroCoreApiTypes.DeleteCompanyResponse;
}

/**
 * Component that displays the number of entities that will be deleted and
 * provides a button to open the corresponding company profile tab in a new browser tab
 */
export function EntityReport({
  companyId,
  tab,
  label,
  deleteWorkspaceResponse,
}: EntityReportProps): React.JSX.Element {
  const summary =
    deleteWorkspaceResponse && "summary" in deleteWorkspaceResponse
      ? deleteWorkspaceResponse.summary
      : undefined;

  return (
    <Grid
      container
      sx={{ justifyContent: "space-between", alignItems: "center" }}
    >
      <Grid>
        <Text variant="bodyM">
          Number of {label} that will be deleted:
          <b style={{ marginLeft: 1 }}>
            {label === "groups"
              ? (summary?.groupCount ?? 0)
              : (summary?.projectCount ?? 0)}
          </b>
        </Text>
      </Grid>
      <Grid>
        <InternalLinkButton
          type="COMPANY"
          companyId={companyId}
          companyProfileTab={tab}
          location="delete workspace"
          shouldOpenInNewTab={true}
        />
      </Grid>
    </Grid>
  );
}
